// src/pages/ambulance/LiveTracking.js
import React, { useState, useEffect, useRef } from 'react';
import apiService from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  MapPin,
  Navigation,
  Clock,
  User,
  Phone,
  AlertCircle,
  CheckCircle,
  Truck,
  Activity,
  TrendingUp,
  Route,
  Timer,
  MessageCircle,
  RefreshCw
} from 'lucide-react';

const LiveTracking = () => {
  const [loading, setLoading] = useState(true);
  const [activeRequests, setActiveRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [error, setError] = useState(null);
  const [updateInterval, setUpdateInterval] = useState(null);
  const mapRef = useRef(null);

  useEffect(() => {
    fetchActiveRequests();
    
    // Auto-refresh every 10 seconds
    const interval = setInterval(() => {
      fetchActiveRequests();
    }, 10000);
    
    setUpdateInterval(interval);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (selectedRequest && mapRef.current) {
      initializeMap();
    }
  }, [selectedRequest]);

  const fetchActiveRequests = async () => {
    try {
      const response = await apiService.ambulances.getRequests({ 
        status: 'accepted,en_route_pickup,arrived_pickup,patient_loaded,en_route_dropoff,arrived_dropoff' 
      });
      
      if (response.success) {
        setActiveRequests(response.data.requests || []);
        
        // If a request is selected, update it
        if (selectedRequest) {
          const updated = response.data.requests.find(r => r._id === selectedRequest._id);
          if (updated) {
            setSelectedRequest(updated);
          }
        }
      }
    } catch (err) {
      console.error('Fetch active requests error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const initializeMap = () => {
    // Basic map initialization placeholder
    // In production, integrate with Google Maps or Mapbox
    console.log('Map initialized for request:', selectedRequest._id);
  };

  const getStatusColor = (status) => {
    const colors = {
      accepted: 'bg-blue-100 text-blue-800 border-blue-200',
      en_route_pickup: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      arrived_pickup: 'bg-orange-100 text-orange-800 border-orange-200',
      patient_loaded: 'bg-purple-100 text-purple-800 border-purple-200',
      en_route_dropoff: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      arrived_dropoff: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status) => {
    const icons = {
      accepted: CheckCircle,
      en_route_pickup: Navigation,
      arrived_pickup: MapPin,
      patient_loaded: Truck,
      en_route_dropoff: Navigation,
      arrived_dropoff: MapPin
    };
    const Icon = icons[status] || Activity;
    return <Icon className="w-4 h-4" />;
  };

  const getStatusLabel = (status) => {
    const labels = {
      accepted: 'Request Accepted',
      en_route_pickup: 'En Route to Pickup',
      arrived_pickup: 'Arrived at Pickup',
      patient_loaded: 'Patient Loaded',
      en_route_dropoff: 'En Route to Destination',
      arrived_dropoff: 'Arrived at Destination'
    };
    return labels[status] || status;
  };

  const calculateElapsedTime = (startTime) => {
    if (!startTime) return 'N/A';
    
    const start = new Date(startTime);
    const now = new Date();
    const diff = Math.floor((now - start) / 1000 / 60); // minutes
    
    if (diff < 60) {
      return `${diff} min ago`;
    } else {
      const hours = Math.floor(diff / 60);
      const mins = diff % 60;
      return `${hours}h ${mins}m ago`;
    }
  };

  const formatDistance = (distance) => {
    if (!distance) return 'N/A';
    return `${distance.toFixed(1)} km`;
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchActiveRequests();
  };

  const handleUpdateStatus = async (requestId, newStatus, notes) => {
    try {
      const response = await apiService.ambulances.updateRequestStatus(requestId, {
        status: newStatus,
        notes
      });

      if (response.success) {
        fetchActiveRequests();
      }
    } catch (err) {
      console.error('Update status error:', err);
      setError(err.message);
    }
  };

  const handleContactPatient = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  if (loading && activeRequests.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Live Tracking</h1>
          <p className="text-gray-600 mt-1">
            Monitor active ambulance requests in real-time
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Active Requests Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Activity className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Active Requests</p>
              <p className="text-2xl font-bold text-gray-900">
                {activeRequests.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Navigation className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">En Route</p>
              <p className="text-2xl font-bold text-gray-900">
                {activeRequests.filter(r => 
                  r.status === 'en_route_pickup' || r.status === 'en_route_dropoff'
                ).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <MapPin className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">At Location</p>
              <p className="text-2xl font-bold text-gray-900">
                {activeRequests.filter(r => 
                  r.status === 'arrived_pickup' || r.status === 'arrived_dropoff'
                ).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Truck className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Patient Loaded</p>
              <p className="text-2xl font-bold text-gray-900">
                {activeRequests.filter(r => r.status === 'patient_loaded').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {activeRequests.length === 0 ? (
        /* No Active Requests */
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <Activity className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No Active Requests
          </h3>
          <p className="text-gray-600">
            All requests have been completed or there are no ongoing trips at the moment.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Requests List */}
          <div className="lg:col-span-1 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Active Requests</h2>
            
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {activeRequests.map((request) => (
                <div
                  key={request._id}
                  onClick={() => setSelectedRequest(request)}
                  className={`bg-white rounded-lg shadow-sm p-4 cursor-pointer transition-all border-2 ${
                    selectedRequest?._id === request._id
                      ? 'border-red-500 shadow-md'
                      : 'border-transparent hover:border-gray-300'
                  }`}
                >
                  {/* Status Badge */}
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium border mb-3 ${getStatusColor(request.status)}`}>
                    {getStatusIcon(request.status)}
                    {getStatusLabel(request.status)}
                  </div>

                  {/* Patient Info */}
                  <div className="space-y-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">
                          {request.patientInfo?.name}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        #{request._id.slice(-6)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>{calculateElapsedTime(request.acceptedAt)}</span>
                    </div>

                    {request.estimatedDistance && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Route className="w-4 h-4" />
                        <span>{formatDistance(request.estimatedDistance)}</span>
                      </div>
                    )}

                    {/* Priority Badge */}
                    {request.priority === 'critical' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">
                        <AlertCircle className="w-3 h-3" />
                        Critical
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Request Details */}
          <div className="lg:col-span-2">
            {selectedRequest ? (
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-red-600 to-orange-600 px-6 py-4">
                  <div className="flex items-center justify-between text-white">
                    <div>
                      <h2 className="text-xl font-bold">Request Details</h2>
                      <p className="text-red-100 text-sm mt-1">
                        ID: #{selectedRequest._id.slice(-8)}
                      </p>
                    </div>
                    <div className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(selectedRequest.status)}`}>
                      {getStatusLabel(selectedRequest.status)}
                    </div>
                  </div>
                </div>

                {/* Map Placeholder */}
                <div ref={mapRef} className="h-64 bg-gray-100 border-b border-gray-200">
                  <div className="h-full flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <MapPin className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm">Map integration placeholder</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Integrate with Google Maps or Mapbox API
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  {/* Patient Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <User className="w-5 h-5 text-red-600" />
                      Patient Information
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Name</p>
                        <p className="font-medium text-gray-900">
                          {selectedRequest.patientInfo?.name}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Age</p>
                        <p className="font-medium text-gray-900">
                          {selectedRequest.patientInfo?.age || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Gender</p>
                        <p className="font-medium text-gray-900">
                          {selectedRequest.patientInfo?.gender || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Contact</p>
                        <button
                          onClick={() => handleContactPatient(selectedRequest.patientInfo?.phone)}
                          className="flex items-center gap-1 text-red-600 hover:text-red-700 font-medium"
                        >
                          <Phone className="w-4 h-4" />
                          {selectedRequest.patientInfo?.phone}
                        </button>
                      </div>
                      <div className="col-span-2">
                        <p className="text-sm text-gray-600">Condition</p>
                        <p className="font-medium text-gray-900">
                          {selectedRequest.patientInfo?.condition}
                        </p>
                      </div>
                      {selectedRequest.patientInfo?.symptoms && (
                        <div className="col-span-2">
                          <p className="text-sm text-gray-600">Symptoms</p>
                          <p className="text-gray-900">
                            {selectedRequest.patientInfo.symptoms}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Locations */}
                  <div className="pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-red-600" />
                      Locations
                    </h3>
                    <div className="space-y-4">
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <MapPin className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">Pickup Location</p>
                          <p className="text-sm text-gray-600 mt-1">
                            {selectedRequest.pickupLocation?.address}
                          </p>
                          {selectedRequest.pickupLocation?.landmark && (
                            <p className="text-xs text-gray-500 mt-1">
                              Landmark: {selectedRequest.pickupLocation.landmark}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <MapPin className="w-4 h-4 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">Destination</p>
                          <p className="text-sm text-gray-600 mt-1">
                            {selectedRequest.dropoffLocation?.address}
                          </p>
                          {selectedRequest.dropoffLocation?.facilityName && (
                            <p className="text-xs text-gray-500 mt-1">
                              Facility: {selectedRequest.dropoffLocation.facilityName}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Vehicle & Driver */}
                  <div className="pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Truck className="w-5 h-5 text-red-600" />
                      Assigned Resources
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedRequest.vehicle && (
                        <div>
                          <p className="text-sm text-gray-600">Vehicle</p>
                          <p className="font-medium text-gray-900">
                            {selectedRequest.vehicle.registrationNumber || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {selectedRequest.vehicle.vehicleType || 'Ambulance'}
                          </p>
                        </div>
                      )}
                      {selectedRequest.driver && (
                        <div>
                          <p className="text-sm text-gray-600">Driver</p>
                          <p className="font-medium text-gray-900">
                            {selectedRequest.driver.firstName} {selectedRequest.driver.lastName}
                          </p>
                          <button
                            onClick={() => handleContactPatient(selectedRequest.driver.phone)}
                            className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1 mt-1"
                          >
                            <Phone className="w-3 h-3" />
                            {selectedRequest.driver.phone}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Timer className="w-5 h-5 text-red-600" />
                      Timeline
                    </h3>
                    <div className="space-y-3">
                      {selectedRequest.statusHistory?.map((status, index) => (
                        <div key={index} className="flex gap-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                              {getStatusIcon(status.status)}
                            </div>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {getStatusLabel(status.status)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(status.timestamp).toLocaleString()}
                            </p>
                            {status.notes && (
                              <p className="text-sm text-gray-600 mt-1">{status.notes}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Pricing */}
                  {selectedRequest.pricing && (
                    <div className="pt-6 border-t border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-red-600" />
                        Pricing
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Base Fee</span>
                            <span className="font-medium">
                              KES {selectedRequest.pricing.baseFee?.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Distance Fee</span>
                            <span className="font-medium">
                              KES {selectedRequest.pricing.distanceFee?.toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-300">
                            <span>Total</span>
                            <span className="text-red-600">
                              KES {selectedRequest.pricing.totalAmount?.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Quick Actions */}
                  <div className="pt-6 border-t border-gray-200">
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleContactPatient(selectedRequest.patientInfo?.phone)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Phone className="w-4 h-4" />
                        Call Patient
                      </button>
                      {selectedRequest.driver && (
                        <button
                          onClick={() => handleContactPatient(selectedRequest.driver.phone)}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <MessageCircle className="w-4 h-4" />
                          Contact Driver
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center h-full flex items-center justify-center">
                <div>
                  <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Select a Request
                  </h3>
                  <p className="text-gray-600">
                    Choose a request from the list to view details and track in real-time
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveTracking;